import { Position, Square, BOARD_SIZE } from '@draughts/shared';

/**
 * Numbering schemes:
 * - Dark-only (default): squares numbered 1..N²/2, only dark squares playable.
 *   International (10×10): dark square in bottom-left, odd rows have even cols.
 *   Italian (8×8): dark square in bottom-right, odd rows have odd cols.
 * - All-squares (Turkish/Dameo/Armenian/Croda): squares numbered 1..N² row-major,
 *   left-to-right, top-to-bottom; every square is playable.
 */
export function squareToPosition(
  square: Square,
  boardSize: number = BOARD_SIZE,
  darkSquareBottomRight: boolean = boardSize === 8,
  allSquaresPlayable: boolean = false,
): Position {
  if (allSquaresPlayable) {
    const maxSquares = boardSize * boardSize;
    if (!Number.isInteger(square) || square < 1 || square > maxSquares) {
      throw new Error(`Invalid square: ${square}. Must be between 1 and ${maxSquares}.`);
    }
    const row = Math.ceil(square / boardSize);
    const col = ((square - 1) % boardSize) + 1;
    return { row, col };
  }

  const squaresPerRow = boardSize / 2;
  const maxSquares = boardSize * squaresPerRow;

  if (!isValidSquare(square, boardSize)) {
    throw new Error(`Invalid square: ${square}. Must be between 1 and ${maxSquares}.`);
  }

  const row = Math.ceil(square / squaresPerRow);
  const posInRow = (square - 1) % squaresPerRow;
  const isItalian = darkSquareBottomRight;

  let col: number;
  if (isItalian) {
    col = row % 2 === 1
      ? posInRow * 2 + 1
      : posInRow * 2 + 2;
  } else {
    col = row % 2 === 1
      ? posInRow * 2 + 2
      : posInRow * 2 + 1;
  }

  return { row, col };
}

export function positionToSquare(
  position: Position,
  boardSize: number = BOARD_SIZE,
  darkSquareBottomRight: boolean = boardSize === 8,
  allSquaresPlayable: boolean = false,
): Square | null {
  const { row, col } = position;

  if (row < 1 || row > boardSize || col < 1 || col > boardSize) {
    return null;
  }

  if (allSquaresPlayable) {
    return (row - 1) * boardSize + col;
  }

  if (!isPlayablePosition(position, boardSize, darkSquareBottomRight)) {
    return null;
  }

  const squaresPerRow = boardSize / 2;
  const squaresBeforeThisRow = (row - 1) * squaresPerRow;
  const isItalian = darkSquareBottomRight;

  let posInRow: number;
  if (isItalian) {
    posInRow = row % 2 === 1
      ? (col - 1) / 2
      : (col - 2) / 2;
  } else {
    posInRow = row % 2 === 1
      ? (col - 2) / 2
      : (col - 1) / 2;
  }

  return squaresBeforeThisRow + posInRow + 1;
}

export function isValidSquare(square: Square, boardSize: number = BOARD_SIZE): boolean {
  const maxSquares = boardSize * (boardSize / 2);
  return Number.isInteger(square) && square >= 1 && square <= maxSquares;
}

export function isPlayablePosition(position: Position, boardSize: number = BOARD_SIZE, darkSquareBottomRight: boolean = boardSize === 8): boolean {
  const { row, col } = position;

  if (row < 1 || row > boardSize || col < 1 || col > boardSize) {
    return false;
  }

  const isItalian = darkSquareBottomRight;

  if (isItalian) {
    return (row + col) % 2 === 0;
  } else {
    return (row + col) % 2 === 1;
  }
}

export function getAdjacentSquares(square: Square, boardSize: number = BOARD_SIZE, darkSquareBottomRight: boolean = boardSize === 8): Square[] {
  const pos = squareToPosition(square, boardSize, darkSquareBottomRight);
  const adjacents: Square[] = [];

  const directions = [
    { row: -1, col: -1 },
    { row: -1, col: +1 },
    { row: +1, col: -1 },
    { row: +1, col: +1 },
  ];

  for (const dir of directions) {
    const newPos: Position = {
      row: pos.row + dir.row,
      col: pos.col + dir.col,
    };
    const newSquare = positionToSquare(newPos, boardSize, darkSquareBottomRight);
    if (newSquare !== null) {
      adjacents.push(newSquare);
    }
  }

  return adjacents;
}

export function getBoardSize(variant: string): number {
  return (variant === 'ITALIAN' || variant === 'ENGLISH') ? 8 : 10;
}
